import { useMemo } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';

/**
 * Signale les champs obligatoires encore vides, d'apres le serveur.
 *
 * L'asterisque du formulaire etait purement decorative : le calcul de completion
 * cote serveur et les champs marques obligatoires cote formulaire etaient deux
 * listes distinctes, jamais resynchronisees. Un prestataire pouvait laisser un
 * champ vide, rester a 100 %, et n'en etre averti nulle part.
 *
 * Ce hook derive donc l'etat visuel de `completion.missing`, la liste que le
 * serveur calcule deja et renvoie a chaque chargement. Aucune liste de champs
 * obligatoires n'est redeclaree cote front : ajouter une exigence au calcul
 * serveur suffit a la faire apparaitre a l'ecran.
 *
 * Les cles de `missing` sont les noms de colonnes, identiques aux attributs
 * `name` des inputs (`part_ca_n1`, `numero_tva`, `compta_email`...).
 */
export interface ChampsManquants {
  /** true si ce champ est obligatoire et vide selon le serveur. */
  estManquant: (field: string) => boolean;
  /**
   * Classe CSS a poser sur un input.
   *
   * @param field       nom du champ (= attribut `name` de l'input)
   * @param aUneErreur  true si le serveur a renvoye une erreur de validation
   *                    sur ce champ ; elle prime sur le simple manque.
   */
  classeChamp: (field: string, aUneErreur?: boolean) => string;
  /** Nombre de champs obligatoires vides, toutes sections confondues. */
  total: number;
}

export function useChampsManquants(): ChampsManquants {
  const { completion } = useProfile();

  return useMemo(() => {
    const manquants = new Set((completion?.missing ?? []).map(item => item.field));

    const estManquant = (field: string) => manquants.has(field);

    return {
      estManquant,
      classeChamp: (field, aUneErreur = false) =>
        aUneErreur || estManquant(field) ? 'input-error' : '',
      total: manquants.size,
    };
  }, [completion]);
}
